// src/pages/church/ChurchJournals.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Navbar from "../../components/common/Navbar";
import HeaderBar from "../../components/common/HeaderBar";
import JournalsList from "../journals/JournalsList";
import { readChurchesAction } from "../../store/Church/ChurchActions";
import LoaderSpinner from "../../components/common/LoaderSpinner";
import { FaArrowLeft } from "react-icons/fa";

const ChurchJournals = () => {
  const { churchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const { isMobile } = useSelector((state) => state.common);
  
  const [churchData, setChurchData] = useState(null);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (churchId) {
      fetchChurchJournals();
    } else {
      setError("Church ID not found");
      setLoading(false);
    }
  }, [churchId]);

  const fetchChurchJournals = () => {
    setLoading(true);
    setError(null);
    
    dispatch(
      readChurchesAction({
        apiPayloadRequest: { _id: churchId },
        callback: (res) => {
          if (res.type === 1 && res.data?.[0]) {
            const church = res.data[0];
            setChurchData(church);
            
            // Extract and transform journals
            const extractedJournals = transformChurchJournals(church?.journals || []);
            setJournals(extractedJournals);
          } else {
            setError("Failed to load church data");
          }
          setLoading(false);
        },
      })
    );
  };

  const transformChurchJournals = (journalsArray) => {
    return journalsArray.map((item) => {
      // Find thumbnail image
      const thumbnail = item?.bg_Images?.find((img) => img?.type_of_url === "1") || 
                       item?.journalcoverImage?.[0]?.gallery || 
                       item?.coverImage?.[0]?.gallery;
      
      return {
        _id: item?._id || item?.journalObjId,
        journalObjId: item?.journalObjId,
        journal_title: item?.journal_title || item?.journalName || "Journal",
        author: item?.author,
        journal_description: item?.journal_description || item?.description || "",
        bg_Images: thumbnail ? [{ 
          url_link: thumbnail?.imageUrl || thumbnail?.url_link || "",
          type_of_url: "1"
        }] : [],
        createdDate: item?.createdDate,
        category: item?.category || "General",
        // Original data for reference
        originalData: item
      };
    });
  };

  const handleBack = () => {
    navigate(`/church/detail/${churchId}`);
  };

  const handleJournalClick = (journalId) => {
    navigate(`/journals/${journalId}`);
  };

  const handleShareAll = () => {
    // Share functionality for all journals
    const shareText = `Check out journals from ${churchData?.churchName}`;
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: `${churchData?.churchName} - Journals`,
        text: shareText,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      // You can add a toast notification here
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="flex w-full flex-col overflow-hidden md:flex-row bg-black min-h-screen">
        <Navbar active={"Journals"} />
        <div className="flex min-h-screen w-full flex-col md:ml-[104px] bg-black">
          <HeaderBar
            popCancle={true}
            cancelBtn={false}
            stepBack={true}
            back={true}
            backLink={`/church/detail/${churchId}`}
          />
          <div className="flex h-[60vh] w-full items-center justify-center bg-black">
            <LoaderSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full flex-col overflow-hidden md:flex-row bg-black min-h-screen">
        <Navbar active={"Journals"} />
        <div className="flex min-h-screen w-full flex-col md:ml-[104px] bg-black">
          <HeaderBar
            popCancle={true}
            cancelBtn={false}
            stepBack={true}
            back={true}
            backLink={`/church/detail/${churchId}`}
          />
          <div className="flex h-[60vh] w-full flex-col items-center justify-center p-8 bg-black">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-4">{error}</h3>
              <button
                onClick={handleBack}
                className="mt-4 px-6 py-2 bg-[#F8BA34] text-black rounded-full hover:bg-yellow-500 transition-colors flex items-center gap-2"
              >
                <FaArrowLeft /> Back to Church
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col overflow-hidden md:flex-row bg-black min-h-screen">
      <Navbar active={"Journals"} />
      <div className="flex min-h-screen w-full flex-col md:ml-[104px] bg-black">
        <HeaderBar
          popCancle={true}
          cancelBtn={false}
          stepBack={true}
          back={true}
          backLink={`/church/detail/${churchId}`}
          active={0}
          handleTabChange={() => {}}
        />
        
        <div className="flex w-full flex-1 flex-col overflow-y-auto bg-black p-6 md:p-8">
         
          
          {/* Journals Grid */}
          {journals.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[#F8BA34]">
                  All Journals
                </h2>
                <span className="text-gray-400">
                  {journals.length} items
                </span>
              </div>
              
              <JournalsList 
                gridItems={isMobile ? 2 : 8} 
                data={journals}
                chapter={false}
              />
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-800 mb-6">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-10 w-10 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" 
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                No Journals Available
              </h3>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                This church hasn't published any journals yet. Check back later for new content.
              </p>
              <button
                onClick={handleBack}
                className="px-6 py-3 bg-[#F8BA34] text-black rounded-full hover:bg-yellow-500 transition-colors font-medium"
              >
                Back to Church Details
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChurchJournals;